import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Button,
  Col,
  Empty,
  Input,
  message,
  Modal,
  Row,
  Select,
  Spin,
  Tag,
  Typography,
} from "antd";
import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  CrownOutlined,
  DatabaseOutlined,
  ReloadOutlined,
  ShopOutlined,
  SwapOutlined,
  TeamOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  formatSubscriptionDate,
  formatSubscriptionNumber,
} from "../subscriptionFormatters";
import "./SubscriptionPlanChangeModal.css";

const { Text } = Typography;
const { TextArea } = Input;

interface Plan {
  id: number;
  name: string;
  description: string | null;
  price: number;
  currency: string;
  billing_cycle: "monthly" | "quarterly" | "annual";
  features: string[];
  max_users: number | null;
  max_companies: number | null;
  storage_limit_gb: number | null;
  is_trial: boolean;
  is_active: boolean;
  sort_order: number;
}

interface Company {
  id: number;
  name: string;
  email?: string | null;
  subscription_plan_id: number | null;
  plan_name?: string | null;
  plan_price?: number | null;
  plan_currency?: string | null;
  plan_billing_cycle?: string | null;
  licence_expire_on?: string | null;
  trial_ends_at?: string | null;
}

interface CompanyDetails extends Company {
  plan_description: string | null;
  plan_features: string[];
  plan_max_users: number | null;
  plan_max_companies: number | null;
  plan_storage_limit: number | null;
  plan_sort_order: number | null;
}

interface PlanUpgradeModalProps {
  visible: boolean;
  company: Company | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const planLimit = (
  value: number | null | undefined,
  suffix = "",
) => (value ? `${formatSubscriptionNumber(value)}${suffix}` : "Illimité");

const PlanUpgradeModal: React.FC<PlanUpgradeModalProps> = ({
  visible,
  company,
  onSuccess,
  onCancel,
}) => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [companyDetails, setCompanyDetails] = useState<CompanyDetails | null>(
    null,
  );
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(
    async (signal?: AbortSignal) => {
      if (!company?.id) return;

      setLoading(true);
      setError(null);

      try {
        const headers = {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          "Content-Type": "application/json",
        };
        const [plansResponse, detailsResponse] = await Promise.all([
          fetch("/api/subscription/plans?status=active&limit=100", {
            headers,
            signal,
          }),
          fetch(`/api/subscription/companies/${company.id}`, {
            headers,
            signal,
          }),
        ]);
        const [plansPayload, detailsPayload] = await Promise.all([
          plansResponse.json(),
          detailsResponse.json(),
        ]);

        if (!plansResponse.ok || !plansPayload.success) {
          throw new Error(plansPayload.message || "Impossible de charger les plans");
        }
        if (!detailsResponse.ok || !detailsPayload.success) {
          throw new Error(
            detailsPayload.message ||
              "Impossible de charger la souscription actuelle",
          );
        }

        setPlans(
          (plansPayload.data.plans || []).filter(
            (plan: Plan) => Boolean(plan.is_active),
          ),
        );
        setCompanyDetails(detailsPayload.data.company);
      } catch (requestError) {
        if ((requestError as Error).name !== "AbortError") {
          setError(
            requestError instanceof Error
              ? requestError.message
              : "Impossible de préparer le changement de plan",
          );
        }
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [company?.id],
  );

  useEffect(() => {
    if (!visible || !company?.id) {
      setPlans([]);
      setCompanyDetails(null);
      setSelectedPlanId(null);
      setReason("");
      setError(null);
      return undefined;
    }

    setSelectedPlanId(null);
    setReason("");
    setPlans([]);
    setCompanyDetails(null);
    const controller = new AbortController();
    loadData(controller.signal);
    return () => controller.abort();
  }, [company?.id, loadData, visible]);

  const availablePlans = useMemo(
    () =>
      plans
        .filter((plan) => plan.id !== companyDetails?.subscription_plan_id)
        .sort((left, right) => left.sort_order - right.sort_order),
    [companyDetails?.subscription_plan_id, plans],
  );

  const selectedPlan = useMemo(
    () => plans.find((plan) => plan.id === selectedPlanId) || null,
    [plans, selectedPlanId],
  );

  const currentPlan = companyDetails
    ? {
        id: companyDetails.subscription_plan_id,
        name: companyDetails.plan_name || "Aucun plan",
        price: companyDetails.plan_price,
        currency: companyDetails.plan_currency || "XOF",
        billingCycle: companyDetails.plan_billing_cycle,
        maxUsers: companyDetails.plan_max_users,
        maxCompanies: companyDetails.plan_max_companies,
        storage: companyDetails.plan_storage_limit,
        sortOrder: companyDetails.plan_sort_order,
        features: companyDetails.plan_features || [],
      }
    : null;

  const isDowngrade =
    Boolean(selectedPlan) &&
    Number(selectedPlan?.sort_order) < Number(currentPlan?.sortOrder ?? 0);

  const submitPlanChange = async () => {
    if (!company || !selectedPlan) return;
    if (reason.trim().length < 3) {
      message.warning("Le motif du changement est obligatoire");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`/api/subscription/companies/${company.id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          subscription_plan_id: selectedPlan.id,
          revision_reason: reason.trim(),
        }),
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Le changement de plan a échoué");
      }

      message.success(`Plan modifié vers ${selectedPlan.name}`);
      onSuccess();
    } catch (requestError) {
      message.error(
        requestError instanceof Error
          ? requestError.message
          : "Erreur de connexion lors du changement de plan",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const modalTitle = (
    <div className="subscription-plan-change-modal__header">
      <Avatar size={52} icon={<SwapOutlined />} />
      <div>
        <div className="subscription-plan-change-modal__heading">
          Changer de plan
        </div>
        <div className="subscription-plan-change-modal__subtitle">
          {company?.name || "Entreprise"}
          {company?.email ? ` · ${company.email}` : ""}
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      centered
      className="subscription-plan-change-modal"
      title={modalTitle}
      open={visible}
      onCancel={onCancel}
      width={860}
      destroyOnClose
      footer={
        <div className="subscription-plan-change-modal__footer">
          <Button disabled={submitting} onClick={onCancel}>
            Annuler
          </Button>
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            loading={submitting}
            disabled={!selectedPlan || Boolean(error) || reason.trim().length < 3}
            onClick={submitPlanChange}
          >
            Appliquer le changement
          </Button>
        </div>
      }
    >
      <Spin spinning={loading} tip="Chargement des plans…">
        {error ? (
          <Alert
            type="error"
            showIcon
            message="Changement de plan indisponible"
            description={error}
            action={
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => loadData()}
              >
                Réessayer
              </Button>
            }
          />
        ) : companyDetails && currentPlan ? (
          <div className="subscription-plan-change-modal__content">
            <Alert
              type="info"
              showIcon
              message="Changement administratif sans renouvellement"
              description={`L'échéance du ${formatSubscriptionDate(
                companyDetails.licence_expire_on || companyDetails.trial_ends_at,
              )} sera conservée et aucune facture ne sera créée.`}
            />

            <div className="subscription-plan-change-modal__flow">
              <div className="subscription-plan-change-modal__plan-card is-current">
                <span className="subscription-plan-change-modal__card-label">
                  Plan actuel
                </span>
                <strong>{currentPlan.name}</strong>
                <span>Fonctionnalités et limites actuelles</span>
                <small>Facturation par Warehouse inchangée</small>
              </div>

              <span className="subscription-plan-change-modal__arrow">
                <ArrowRightOutlined />
              </span>

              <div
                className={`subscription-plan-change-modal__plan-card is-next${
                  selectedPlan ? " has-selection" : ""
                }`}
              >
                <span className="subscription-plan-change-modal__card-label">
                  Nouveau plan
                </span>
                {selectedPlan ? (
                  <>
                    <strong>{selectedPlan.name}</strong>
                    <span>Nouvelles fonctionnalités et limites</span>
                    <small>Facturation par Warehouse inchangée</small>
                  </>
                ) : (
                  <Text type="secondary">À sélectionner</Text>
                )}
              </div>
            </div>

            {availablePlans.length > 0 ? (
              <div className="subscription-plan-change-modal__field">
                <label htmlFor="subscription-plan-select">
                  Sélectionner le nouveau plan
                </label>
                <Select
                  id="subscription-plan-select"
                  showSearch
                  optionFilterProp="label"
                  placeholder="Choisir un plan actif"
                  value={selectedPlanId || undefined}
                  onChange={setSelectedPlanId}
                  options={availablePlans.map((plan) => ({
                    value: plan.id,
                    label: `${plan.name} · fonctionnalités et limites`,
                  }))}
                />
              </div>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Aucun autre plan actif n'est disponible."
              />
            )}

            {selectedPlan && (
              <div className="subscription-plan-change-modal__comparison">
                <div className="subscription-plan-change-modal__comparison-title">
                  <div>
                    <CrownOutlined />
                    <strong>{selectedPlan.name}</strong>
                  </div>
                  {isDowngrade && (
                    <Tag color="warning" icon={<WarningOutlined />}>
                      Réduction de plan
                    </Tag>
                  )}
                </div>

                {selectedPlan.description && <Text>{selectedPlan.description}</Text>}

                <Row gutter={[10, 10]}>
                  <Col xs={24} sm={8}>
                    <div className="subscription-plan-change-modal__limit">
                      <TeamOutlined />
                      <span>Utilisateurs</span>
                      <strong>{planLimit(selectedPlan.max_users)}</strong>
                    </div>
                  </Col>
                  <Col xs={24} sm={8}>
                    <div className="subscription-plan-change-modal__limit">
                      <ShopOutlined />
                      <span>Entreprises</span>
                      <strong>{planLimit(selectedPlan.max_companies)}</strong>
                    </div>
                  </Col>
                  <Col xs={24} sm={8}>
                    <div className="subscription-plan-change-modal__limit">
                      <DatabaseOutlined />
                      <span>Stockage</span>
                      <strong>{planLimit(selectedPlan.storage_limit_gb, " Go")}</strong>
                    </div>
                  </Col>
                </Row>

                {selectedPlan.features?.length > 0 && (
                  <div className="subscription-plan-change-modal__features">
                    {selectedPlan.features.slice(0, 8).map((feature) => (
                      <Tag key={feature}>{feature}</Tag>
                    ))}
                    {selectedPlan.features.length > 8 && (
                      <Tag>+{selectedPlan.features.length - 8} autres</Tag>
                    )}
                  </div>
                )}

                {isDowngrade && (
                  <Alert
                    type="warning"
                    showIcon
                    message="Vérifiez les limites avant de confirmer"
                    description="Le compte peut perdre l'accès à certaines fonctionnalités ou dépasser les nouvelles limites du plan."
                  />
                )}
              </div>
            )}

            <div className="subscription-plan-change-modal__field">
              <label htmlFor="subscription-plan-reason">
                Motif du changement <span>(obligatoire)</span>
              </label>
              <TextArea
                id="subscription-plan-reason"
                rows={3}
                maxLength={500}
                showCount
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Ex. évolution des besoins du client, correction administrative…"
              />
            </div>
          </div>
        ) : (
          <div className="subscription-plan-change-modal__loading-placeholder" />
        )}
      </Spin>
    </Modal>
  );
};

export default PlanUpgradeModal;
